/**
 * 通信契约（单一事实来源）。
 *
 * 两层：
 * 1. 信令层（WebSocket，Cloudflare Worker/Durable Object 或本地 Node 服务器）：
 *    只负责进房、SDP/ICE 路由、在线成员列表；WebRTC 建连后静默，不转发任何业务流。
 * 2. 业务层（RTCDataChannel，教师 ↔ 各辩手）：
 *    双盲发牌、时钟切换裁决、Yjs 状态同步、断言告警等全部走这里。
 *
 * `Participant` 定义的传输形状在两端共享，避免序列化漂移。
 */
import type {
  AssertionAlert,
  DealtCard,
  Participant,
  RoomSnapshot,
  Side,
} from './types.ts';

/* ------------------------------------------------------------------ */
/* 信令层                                                              */
/* ------------------------------------------------------------------ */

export const SIGNALING_PROTOCOL_VERSION = 1;

/** 与 DOM 解耦的 ICE 服务器描述（可直接喂给 RTCPeerConnection） */
export interface IceServerConfig {
  urls: string | string[];
  username?: string;
  credential?: string;
}

export interface SessionDescriptionPayload {
  type: 'offer' | 'answer';
  sdp: string;
}

export interface IceCandidatePayload {
  candidate: string;
  sdpMid: string | null;
  sdpMLineIndex: number | null;
  usernameFragment?: string | null;
}

export type SignalPayload =
  | { kind: 'offer'; description: SessionDescriptionPayload }
  | { kind: 'answer'; description: SessionDescriptionPayload }
  | { kind: 'ice'; candidate: IceCandidatePayload | null };

/** 信令层客户端 → 服务器 */
/** 房间创建时的后端配置（由创建者提交，DO 持久化；加入者从 welcome 读取） */
export interface RoomConfig {
  capacity: number;
  mediaPolicy: 'audio-only' | 'audio-video';
  videoFps: number;
  flowPlanVersion?: number;
}

export const DEFAULT_ROOM_CONFIG: RoomConfig = {
  capacity: 8,
  mediaPolicy: 'audio-video',
  videoFps: 24,
  flowPlanVersion: 1,
};

export type SignalClientMessage =
  | { type: 'join'; roomId: string; peer: Participant; version: number; create?: boolean; config?: RoomConfig }
  | { type: 'signal'; to: string; payload: SignalPayload }
  | { type: 'leave' }
  | { type: 'ping'; t: number };

/** 信令层服务器 → 客户端 */
export type SignalServerMessage =
  | {
      type: 'welcome';
      roomId: string;
      selfId: string;
      peers: Participant[];
      iceServers: IceServerConfig[];
      serverTime: number;
      /** 房主创建时提交的配置；加入者据此显示容量/媒体策略 */
      config?: RoomConfig;
    }
  | { type: 'peer-joined'; peer: Participant }
  | { type: 'peer-left'; peerId: string }
  | { type: 'signal'; from: string; payload: SignalPayload }
  | { type: 'pong'; t: number; serverTime: number }
  | { type: 'error'; code: SignalErrorCode; message: string };

export type SignalErrorCode =
  | 'ROOM_FULL'
  | 'INVALID_ROOM'
  | 'PEER_NOT_FOUND'
  | 'VERSION_MISMATCH'
  | 'ALREADY_JOINED'
  | 'BAD_MESSAGE'
  | 'TEACHER_EXISTS'
  | 'ROOM_NOT_FOUND';

export const SIGNALING_ROOM_CAPACITY = 12;
/** 单房间最多允许的教师连接数（中心节点唯一） */
export const SIGNALING_TEACHER_LIMIT = 1;

/* ------------------------------------------------------------------ */
/* 业务层：RTCDataChannel                                              */
/* ------------------------------------------------------------------ */

export const DATA_CHANNEL_LABEL = 'zongheng-ctrl';
export const DATA_PROTOCOL_VERSION = 1;

/** 时钟同步：客户端四时间戳（NTP 风格），秒/毫秒均为 performance.now() 单调时钟 */
export interface TimeSyncRequest {
  type: 'time-sync';
  t0: number;
  clientId: string;
}

export interface TimeSyncReply {
  type: 'time-sync-reply';
  t0: number;
  /** 教师收到请求的单调时间 */
  t1: number;
  /** 教师发出响应的单调时间 */
  t2: number;
  /** 教师当前服务器 epoch 毫秒（用于换算 runningSince） */
  serverEpochMs: number;
}

/** 辩手端 → 教师端 */
export type DataClientMessage =
  | { type: 'hello'; participant: Participant; protocolVersion: number }
  | {
      type: 'clock-toggle';
      stageIndex: number;
      side: Side;
      /** 客户端 performance.now()，仅用于审计与 RTT 估算 */
      monotonicMs: number;
      clientEpochMs: number;
      /** 乐观更新后的本地视图（教师可据 RTT/2 校正） */
      optimisticRemainingMs: Record<Side, number>;
    }
  | TimeSyncRequest
  | { type: 'assertion-alert'; alert: AssertionAlert }
  | {
      type: 'transcript';
      side: Side;
      text: string;
      isFinal: boolean;
      at: number;
      source: 'MANUAL' | 'ASR';
    }
  | { type: 'manual-evidence-toggle'; side: Side; evidenceId: string; checked: boolean }
  | { type: 'annotation'; annotationId: string; text: string; at: number }
  /** 非教师角色请求完整状态快照（重连时用） */
  | { type: 'request-sync' };

/** 教师端 → 辩手端 */
export type DataServerMessage =
  | {
      type: 'welcome';
      selfId: string;
      roomId: string;
      /** 教师的公开身份信息 */
      teacher: Participant | null;
      participants: Participant[];
      snapshot: RoomSnapshot;
      serverEpochMs: number;
    }
  | { type: 'yjs-sync'; update: string }
  | { type: 'yjs-update'; update: string }
  | { type: 'snapshot'; snapshot: RoomSnapshot }
  | TimeSyncReply
  | {
      type: 'deal';
      pairId: string;
      stageIndex: number;
      side: Side;
      card: DealtCard;
      /** 兑现承诺用的盐值（仅本侧可见，用于赛后自证） */
      salt: string;
      commitmentHash: string;
    }
  | { type: 'reveal'; pairId: string; reveals: unknown[] }
  | { type: 'error'; code: string; message: string };

export type DataChannelMessage = DataClientMessage | DataServerMessage;

/** 业务层信封（方便扩展 seq/ack；目前直接 JSON 序列化消息体） */
export function encodeDataMessage(message: DataChannelMessage): string {
  return JSON.stringify(message);
}

export function decodeDataMessage(raw: string): DataChannelMessage {
  return JSON.parse(raw) as DataChannelMessage;
}

/* ------------------------------------------------------------------ */
/* 房间码                                                              */
/* ------------------------------------------------------------------ */

const ROOM_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

export function normalizeRoomCode(code: string): string {
  return code.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
}

export function generateRoomCode(random: () => number = Math.random): string {
  let code = '';
  for (let i = 0; i < 6; i += 1) {
    const index = Math.floor(random() * ROOM_CODE_ALPHABET.length) % ROOM_CODE_ALPHABET.length;
    code += ROOM_CODE_ALPHABET[index];
  }
  return code;
}

export function isValidRoomCode(code: string): boolean {
  return /^[A-Z0-9]{6}$/.test(normalizeRoomCode(code));
}
